import { Link } from 'react-router-dom';
import { InfoCard } from '@backstage/core-components';
import { useEntity } from '@backstage/plugin-catalog-react';
import { stringifyEntityRef } from '@backstage/catalog-model';
import { RiImageLine } from '@remixicon/react';
import { imageLaunchUrl } from './catalogInfraTypes';
import {
  CopyButton,
  CopyableValue,
  ExternalLink,
  InfraCodeBlock,
  InfraField,
  InfraSection,
  InfraStack,
  InfraTag,
  InfraTagRow,
  Text,
  infraCardStyles as styles,
} from '../ui/InfraPrimitives';

export const GcpImageBuildInfoCard = () => {
  const { entity } = useEntity();

  const annotations = entity.metadata.annotations || {};
  const templateType = annotations['infrastructure.backstage.io/type'];

  if (templateType !== 'gcp-image-build') {
    return null;
  }

  const buildStatus = annotations['infrastructure.backstage.io/build-status'] || 'unknown';
  const imageId = annotations['infrastructure.backstage.io/image-id'];
  const imageName = annotations['infrastructure.backstage.io/gcp-image-name'];
  const imageVersion = annotations['infrastructure.backstage.io/image-version'];
  const imageFamily = annotations['infrastructure.backstage.io/image-family'];
  const imageSelfLink = annotations['infrastructure.backstage.io/image-self-link'];
  const sourceRepo = annotations['infrastructure.backstage.io/source-repo'];
  const sourceRef = annotations['infrastructure.backstage.io/source-ref'];
  const gcpProject = annotations['infrastructure.backstage.io/gcp-project'];
  const gcpRegion = annotations['infrastructure.backstage.io/gcp-region'];
  const buildRunUrl = annotations['infrastructure.backstage.io/build-run-url'];
  const buildCompletedAt = annotations['infrastructure.backstage.io/build-completed-at'];
  const createdBy = annotations['infrastructure.backstage.io/created-by'];

  const statusLabel = buildStatus === 'building' ? '⏳ Building...' : buildStatus === 'success' ? '✅ Success' : `❌ ${buildStatus}`;

  const gcloudDeploy = imageName && gcpProject
    ? `gcloud compute instances create my-vm \\\n  --image=${imageName} \\\n  --project=${gcpProject} \\\n  --zone=${gcpRegion ? `${gcpRegion}-b` : 'us-east1-b'}`
    : null;

  const gcloudFamily = imageFamily && gcpProject
    ? `gcloud compute instances create my-vm \\\n  --image-family=${imageFamily} \\\n  --project=${gcpProject} \\\n  --zone=${gcpRegion ? `${gcpRegion}-b` : 'us-east1-b'}`
    : null;

  const terraformSnippet = gcpProject
    ? `data "google_compute_image" "workshop" {\n  family  = "${imageFamily || 'generic-workshop'}"\n  project = "${gcpProject}"\n}`
    : null;

  return (
    <InfoCard
      title="🖼️ GCP Image Build"
      subheader={`Source: ${sourceRepo || 'N/A'}`}
      icon={<RiImageLine size={20} />}
    >
      <InfraStack>
        <InfraSection title="📊 Build Info">
          <InfraField
            label="Status:"
            value={
              <InfraTagRow>
                <InfraTag>{statusLabel}</InfraTag>
                {buildRunUrl && (
                  <ExternalLink href={buildRunUrl}>View Run</ExternalLink>
                )}
              </InfraTagRow>
            }
            mono={false}
          />
          {createdBy && <InfraField label="Created By:" value={createdBy} />}
          {buildCompletedAt && <InfraField label="Completed At:" value={buildCompletedAt} />}
          {sourceRepo && (
            <InfraField
              label="Source Repo:"
              value={<ExternalLink href={sourceRepo}>{sourceRepo}</ExternalLink>}
              mono={false}
            />
          )}
          {sourceRef && <InfraField label="Branch/Ref:" value={sourceRef} />}
        </InfraSection>

        {imageId && (
          <InfraSection title="🖼️ Image Details">
            <CopyableValue label="Image ID:" value={imageId} />
            {imageName && <CopyableValue label="Image Name:" value={imageName} />}
            {imageVersion && <InfraField label="Version:" value={imageVersion} />}
            {imageFamily && <CopyableValue label="Image Family:" value={imageFamily} />}
            {gcpProject && <InfraField label="GCP Project:" value={gcpProject} />}
            {gcpRegion && <InfraField label="Region:" value={gcpRegion} />}
            {imageSelfLink && (
              <InfraField
                label="Self Link:"
                value={
                  <span className={styles.copyRow}>
                    <ExternalLink href={imageSelfLink}>{imageSelfLink}</ExternalLink>
                    <CopyButton text={imageSelfLink} />
                  </span>
                }
                mono={false}
              />
            )}
          </InfraSection>
        )}

        {imageName && gcpProject && (
          <InfraSection title="📋 Usage Instructions">
            {buildStatus === 'success' && imageFamily && (
              <InfraField
                label="Launch VM:"
                value={
                  <Link to={imageLaunchUrl(stringifyEntityRef(entity))}>
                    Launch VM from this image
                  </Link>
                }
                mono={false}
              />
            )}
            {gcloudDeploy && (
              <>
                <Text variant="title-small">Deploy with gcloud:</Text>
                <div style={{ position: 'relative' }}>
                  <InfraCodeBlock>{gcloudDeploy}</InfraCodeBlock>
                  <div style={{ position: 'absolute', top: 4, right: 4 }}>
                    <CopyButton text={gcloudDeploy} />
                  </div>
                </div>
              </>
            )}
            {gcloudFamily && (
              <>
                <Text variant="title-small" style={{ marginTop: '0.75rem' }}>
                  Or use image family (recommended):
                </Text>
                <div style={{ position: 'relative' }}>
                  <InfraCodeBlock>{gcloudFamily}</InfraCodeBlock>
                  <div style={{ position: 'absolute', top: 4, right: 4 }}>
                    <CopyButton text={gcloudFamily} />
                  </div>
                </div>
              </>
            )}
            {terraformSnippet && (
              <>
                <Text variant="title-small" style={{ marginTop: '0.75rem' }}>
                  Terraform data source:
                </Text>
                <div style={{ position: 'relative' }}>
                  <InfraCodeBlock>{terraformSnippet}</InfraCodeBlock>
                  <div style={{ position: 'absolute', top: 4, right: 4 }}>
                    <CopyButton text={terraformSnippet} />
                  </div>
                </div>
              </>
            )}
          </InfraSection>
        )}
      </InfraStack>
    </InfoCard>
  );
};
